import React, {useState} from "react";
import {DownOutlined, NodeIndexOutlined} from "@ant-design/icons";
import {Text, TextAs, TextSizes} from "@clockout-dev/design-elements";
import SectionCard from "../../common/sectionCard/SectionCard";
import {SIDE_EFFECT_META, SIDE_EFFECT_FALLBACK} from "../uploads-simulator.constants";
import pillStyles from "../styles/pills.module.scss";
import styles from "../styles/results.module.scss";

/** One ledger entry with its own expand toggle for the raw payload. */
function LedgerEntry({entry}) {
    const [expanded, setExpanded] = useState(false);
    const meta = SIDE_EFFECT_META[entry?.kind] || SIDE_EFFECT_FALLBACK;
    const hasPayload = entry?.payload !== null && entry?.payload !== undefined;

    return (
        <div className={styles.ledgerRow}>
            <span className={`${styles.ledgerDot} ${styles[`ledgerDot_${meta.tone}`]}`}>{meta.icon}</span>
            <div className={styles.ledgerBody}>
                <div className={styles.ledgerHeadline}>
                    <Text as={TextAs.SPAN} size={TextSizes.EXTRA_SMALL} className={`${pillStyles.tag} ${pillStyles[`tone_${meta.tone}`]}`}>
                        {meta.label}
                    </Text>
                    {typeof entry?.seq === "number" ? (
                        <Text as={TextAs.SPAN} size={TextSizes.EXTRA_SMALL} className={styles.ledgerSeq}>
                            #{entry.seq}
                        </Text>
                    ) : null}
                    {entry?.at ? (
                        <Text as={TextAs.SPAN} size={TextSizes.EXTRA_SMALL} className={styles.ledgerAt}>
                            {entry.at}
                        </Text>
                    ) : null}
                </div>
                <Text as={TextAs.P} size={TextSizes.SMALL} className={styles.ledgerSummary}>
                    {entry?.summary || entry?.kind || "—"}
                </Text>
                {hasPayload ? (
                    <button type="button" className={styles.ledgerToggle} onClick={() => setExpanded(previous => !previous)}>
                        <DownOutlined className={expanded ? styles.ledgerChevronOpen : styles.ledgerChevron} />
                        {expanded ? "Hide payload" : "Show payload"}
                    </button>
                ) : null}
                {expanded && hasPayload ? <pre className={styles.ledgerPayload}>{JSON.stringify(entry.payload, null, 2)}</pre> : null}
            </div>
        </div>
    );
}

/**
 * "What would have happened" — the ordered side-effect ledger as a vertical
 * dot-rail timeline. Every summary is pre-phrased by the service ("Would
 * store…", "Would upsert…") because none of it actually executed.
 *
 * @param {Object} props
 * @param {Object[]} [props.sideEffects] — report `side_effects`
 */
function SideEffectLedger({sideEffects}) {
    const entries = Array.isArray(sideEffects) ? sideEffects : [];

    return (
        <SectionCard icon={<NodeIndexOutlined />} title={`Side-Effect Ledger (${entries.length})`}>
            {entries.length ? (
                <div className={styles.ledgerRail}>
                    {entries.map((entry, index) => (
                        <LedgerEntry key={entry?.seq ?? index} entry={entry} />
                    ))}
                </div>
            ) : (
                <Text as={TextAs.P} size={TextSizes.EXTRA_SMALL} className={styles.emptyNote}>
                    No side effects were recorded for this run.
                </Text>
            )}
        </SectionCard>
    );
}

export default SideEffectLedger;
